import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ChatMessages from "../Chat/ChatMessages";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import RichTextRenderer from "../../molecules/Composer/RichTextRenderer";
import ChatComposer from "../../molecules/ChatComposer";
import ChatConversationHeader from "../Chat/ChatConversationHeader";
import ChatPinnedSummary from "../Chat/ChatPinnedSummary";
import UserCardAvatar from "../../molecules/UserCardAvatar";
import { ReactComponent as MessageIcon } from "../../../images/message-icon.svg";

const KNOWN_PRESENCE_STATUSES = new Set(["online", "offline", "away"]);

const normalizePresenceStatus = (status) => {
  if (typeof status !== "string") {
    return undefined;
  }
  const normalized = status.toLowerCase();
  return KNOWN_PRESENCE_STATUSES.has(normalized) ? normalized : undefined;
};

export default function DirectChatPanel({
  groupName,
  friend,
  messages,
  setMessages,
  messagesRef,
  isLoading,
  hasMoreAbove = false,
  isFetchingOlder = false,
  onLoadOlderMessages,
  currentUserId,
  messageDraft,
  onMessageDraftChange,
  onSendMessage,
  onDeleteMessage,
  onEditMessage,
  onOpenUser,
  mentionFriends = [],
  isSending = false,
  onReactMessage,
  totalMessageCount,
  commentPanelState,
  onCommentPanelStateChange,
  conversationKey,
}) {

  const { t } = useTranslation();
  const isPersonalFriend = Boolean(friend?.isPersonal);

  // Resolve usernames for ChatMessages (DM context): only the friend needs resolving.
  const resolveDmUserName = useCallback((userId) => {
    if (!userId || !friend?.userId) return null;
    if (String(userId) === String(friend.userId)) {
      return { name: friend.username || String(friend.userId), avatarUrl: friend.avatarUrl || null };
    }
    return null; // ChatMessages will label current user with actual name
  }, [friend]);

  const currentUserIdString = currentUserId !== undefined && currentUserId !== null
    ? String(currentUserId)
    : null;

  const resolveDmMessageStatus = useCallback((message) => {
    if (!message || !currentUserIdString) {
      return null;
    }
    const senderId = message.senderId !== undefined && message.senderId !== null
      ? String(message.senderId)
      : null;
    if (senderId !== currentUserIdString) {
      return null;
    }
    const status = typeof message.deliveryStatus === "string"
      ? message.deliveryStatus.toUpperCase()
      : null;
    return status || null;
  }, [currentUserIdString]);

  const friendLabel = friend?.username || t("bandsPage.direct.fallbackName");
  const friendDisplayName = isPersonalFriend
    ? friendLabel
    : (friend?.username ? `@${friend.username}` : friendLabel);
  const presenceUserId = friend?.userId;
  const normalizedPresenceStatus = normalizePresenceStatus(friend?.presenceStatus);
  const fallbackPresenceStatus = friend?.online === undefined ? undefined : (friend.online ? "online" : "offline");
  const resolvedPresenceStatus = normalizedPresenceStatus ?? fallbackPresenceStatus;
  const resolvedPresenceLastSeen = resolvedPresenceStatus === "online" ? null : (friend?.lastSeen ?? null);
  const shouldShowAvatarPresence = Boolean(friend);
  const avatarStatus = shouldShowAvatarPresence
    ? {
        online: resolvedPresenceStatus === "online",
        lastSeen: resolvedPresenceStatus === "online" ? null : resolvedPresenceLastSeen,
        size: "12px",
        offset: "6px",
        placement: "br",
      }
    : undefined;
  const resolvedTotal = Number(totalMessageCount);
  const messageCount = Number.isFinite(resolvedTotal) ? resolvedTotal : messages.length;
  const conversationSecondaryItems = useMemo(() => ([
    {
      id: "messages",
      label: t("bandsPage.direct.messageCount", { count: messageCount }),
      icon: <MessageIcon aria-hidden="true" focusable="false" />,
    },
  ]), [messageCount, t]);

  const conversationActions = useMemo(() => ([
    {
      id: "voice",
      label: t("bandsPage.direct.actions.voice"),
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M6.62 10.79a15.053 15.053 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1 17 17 0 0 1-17-17 1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.56 1 1 0 0 1-.24 1.01z"
          />
        </svg>
      ),
      variant: "ghost",
    },
    {
      id: "video",
      label: t("bandsPage.direct.actions.video"),
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M4 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1.382l3.553 3.553A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-1.447-.894L16 10.276V7a2 2 0 0 0-2-2z"
          />
        </svg>
      ),
      variant: "ghost",
    },
    {
      id: "search",
      label: t("bandsPage.direct.actions.search"),
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M10 4a6 6 0 0 1 4.472 9.984l3.772 3.772a1 1 0 0 1-1.414 1.414l-3.772-3.772A6 6 0 1 1 10 4zm0 2a4 4 0 1 0 4 4 4 4 0 0 0-4-4z"
          />
        </svg>
      ),
      variant: "ghost",
    },
  ]), [t]);

  const groupLabel = groupName || t("bandsPage.channelCreator.header.workspaceFallback");

  return (
    <div className="workspace-surface">
      <header className="channel-header">
        <div className="channel-header__content">
          <ChatConversationHeader
            as="div"
            leading={friend ? (
              <UserCardAvatar
                userId={friend.userId}
                username={friend.username}
                alt={friend.username || t("bandsPage.direct.avatarAlt")}
                avatarUrl={friend.avatarUrl}
                className="chat-conversation__avatar"
                enableProfilePreview={!isPersonalFriend && Boolean(friend.userId)}
                status={avatarStatus}
              />
            ) : null}
            title={friendDisplayName}
            tags={[]}
            secondaryItems={conversationSecondaryItems}
            actions={conversationActions}
            titleAs="h2"
            presenceUserId={presenceUserId}
            presenceStatus={resolvedPresenceStatus}
            presenceLastSeen={resolvedPresenceLastSeen}
            presenceDisplay="expanded"
          />
          <ChatPinnedSummary
            icon={(
              <svg viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M12 3a9 9 0 1 1-9 9 9 9 0 0 1   9-9zm0 2a7 7 0 1 0 7 7 7 7 0 0 0-7-7z"
                  opacity="0.35"
                />
                <path
                  fill="currentColor"
                  d="M11 7h2v5h-2zm0 6h2v2h-2z"
                />
              </svg>
            )}
            title={t("bandsPage.direct.pinned.title")}
            subtitle={t("bandsPage.direct.pinned.subtitle")}
            actionLabel={t("bandsPage.direct.pinned.action")}
          />
        </div>
        <div className="channel-header__status" />
      </header>

      {isLoading ? (
        <div className="workspace-placeholder">
            <LoadingSpinner size={40} />
        </div>
      ) : (
        <ChatMessages
          ref={messagesRef}
          messages={messages}
          setMessages={setMessages}
          currentUserId={currentUserId}
          resolveUserName={resolveDmUserName}
          resolveMessageStatus={resolveDmMessageStatus}
          onReact={(msg, emoji, metadata) => onReactMessage?.(msg, emoji, metadata)}
          onComment={(msg) => { /* open comment drawer */ }}
          onMore={(msg) => { /* open contextual menu */ }}
          onDeleteMessage={onDeleteMessage}
          onEditMessage={onEditMessage}
          hasMoreAbove={hasMoreAbove}
          isFetchingOlder={isFetchingOlder}
          onLoadOlderMessages={onLoadOlderMessages}
          conversationKey={conversationKey}
          commentPanelState={commentPanelState}
          onCommentPanelStateChange={onCommentPanelStateChange}
          contentRenderer={(html) => (
            <RichTextRenderer
              html={html}
              friends={mentionFriends && mentionFriends.length ? mentionFriends : (friend ? [{ userId: friend.userId, username: friend.username }] : [])}
              onOpenUser={onOpenUser}
            />
          )}
        />
      )}

      <ChatComposer
        valueHtml={messageDraft}
        onChangeHtml={onMessageDraftChange}
        isSending={isSending}
        onSend={onSendMessage}
        mentionUsers={friend ? [{ userId: friend.userId, username: friend.username }] : (mentionFriends || [])}
        mentionChannels={[]}
        ariaLabel={t("bandsPage.direct.composerAria")}
        placeholderTarget={groupLabel}
      />
    </div>
  );
}

DirectChatPanel.propTypes = {
  groupName: PropTypes.string,
  friend: PropTypes.shape({
    userId: PropTypes.string,
    username: PropTypes.string,
    online: PropTypes.bool,
    lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    presenceStatus: PropTypes.oneOf(["online", "offline", "away", "unknown"]),
    isPersonal: PropTypes.bool,
    avatarUrl: PropTypes.string,
  }),
  messages: PropTypes.array.isRequired,
  setMessages: PropTypes.func,
  messagesRef: PropTypes.object,
  isLoading: PropTypes.bool,
  hasMoreAbove: PropTypes.bool,
  isFetchingOlder: PropTypes.bool,
  onLoadOlderMessages: PropTypes.func,
  currentUserId: PropTypes.string,
  messageDraft: PropTypes.string.isRequired,
  onMessageDraftChange: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onDeleteMessage: PropTypes.func,
  onEditMessage: PropTypes.func,
  onOpenUser: PropTypes.func,
  mentionFriends: PropTypes.array,
  isSending: PropTypes.bool,
  onReactMessage: PropTypes.func,
  totalMessageCount: PropTypes.number,
  commentPanelState: PropTypes.shape({
    open: PropTypes.bool,
    messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    message: PropTypes.object,
  }),
  onCommentPanelStateChange: PropTypes.func,
  conversationKey: PropTypes.string,
};

DirectChatPanel.defaultProps = {
  groupName: "",
  friend: undefined,
  isLoading: false,
  hasMoreAbove: false,
  isFetchingOlder: false,
  onLoadOlderMessages: undefined,
  currentUserId: undefined,
  setMessages: undefined,
  onDeleteMessage: undefined,
  onEditMessage: undefined,
  onOpenUser: undefined,
  mentionFriends: undefined,
  isSending: false,
  onReactMessage: undefined,
  totalMessageCount: undefined,
  commentPanelState: null,
  onCommentPanelStateChange: undefined,
  conversationKey: undefined,
};
